import { useMutation } from '@tanstack/react-query';
import { pushToast } from '../../lib/events';
import { setSection, setStatus, setStreamStart, type RoomSnapshot } from '../../lib/rooms';
import { HostRailInvites } from './HostRailInvites';
import { HostRailMembers } from './HostRailMembers';
import { HostRailOverlay } from './HostRailOverlay';
import { HostRailSections } from './HostRailSections';
import { HostRailStatus } from './HostRailStatus';

interface HostRailProps {
  snapshot: RoomSnapshot;
}

/**
 * HOST/COHOST-only switcher rail: status transitions, the section stepper, invites, members, and
 * the overlay URL. Stacks below the rating table on mobile (see `routes/Room.tsx`'s layout) rather
 * than a dedicated bottom sheet, which would need its own open/close affordance and gesture
 * handling on top of what the rating table's own mobile layout already does.
 */
export function HostRail({ snapshot }: HostRailProps) {
  const { room, myRole } = snapshot;

  // No invalidate-and-refetch on success: the `room`/`section` STOMP broadcasts these mutations
  // trigger (see `useRoomRealtime`) update the store directly, including for the host's own
  // browser — see `routes/Room.tsx`'s comment on the same pattern for field mutations.
  const statusMutation = useMutation({
    mutationFn: setStatus,
    onError: () => pushToast('Could not change the room status.'),
  });
  const streamStartMutation = useMutation({
    mutationFn: setStreamStart,
    onError: () => pushToast('Could not mark stream start.'),
  });
  const sectionMutation = useMutation({
    mutationFn: setSection,
    onError: () => pushToast('Could not change the current section.'),
  });

  return (
    <aside aria-label="Host controls" className="flex w-full flex-col gap-8 md:w-80 md:shrink-0">
      <HostRailStatus
        room={room}
        myRole={myRole}
        onSetStatus={(status) => statusMutation.mutate({ roomId: room.id, status })}
        onMarkStreamStart={() => streamStartMutation.mutate({ roomId: room.id })}
        pending={statusMutation.isPending || streamStartMutation.isPending}
      />
      <HostRailSections
        template={snapshot.template}
        currentSectionId={room.currentSectionId}
        onSetSection={(sectionId) => sectionMutation.mutate({ roomId: room.id, sectionId })}
        pending={sectionMutation.isPending}
      />
      <HostRailInvites roomId={room.id} />
      <HostRailMembers roomId={room.id} members={snapshot.members} myRole={myRole} />
      <HostRailOverlay roomId={room.id} />
    </aside>
  );
}
